import * as RSwitch from '@radix-ui/react-switch';
import { useId, type ReactNode } from 'react';

/**
 * The track is 44×24, not the 40×22 it was: 22px is under WCAG 2.2 SC 2.5.8's
 * 24×24 minimum, and a browser audit of the built console found two of them on
 * the leads screen and two more inside every content editor. 24px is the floor,
 * not a target — the thumb grew with it so the control still reads as a switch
 * rather than as a stretched pill.
 */

export function Switch({
  label,
  checked,
  onCheckedChange,
  disabled,
  hint,
  note,
  error,
}: {
  label: ReactNode;
  checked: boolean;
  onCheckedChange: (v: boolean) => void;
  disabled?: boolean;
  hint?: ReactNode;
  /** A live line under the hint that the switch depends on (B-R9-02: the quote's
   *  character count beside the home-page tick). */
  note?: ReactNode;
  /** The refusal for this switch's value (a save's field error), shown under it
   *  and announced, as a text field shows its own. */
  error?: ReactNode;
}) {
  const id = useId();
  const noteId = `${id}-note`;
  const errorId = `${id}-error`;
  const describedBy = [note ? noteId : '', error ? errorId : ''].filter(Boolean).join(' ') || undefined;
  return (
    <div className="flex flex-col gap-xs">
      <div className="flex items-start justify-between gap-md">
        {/* `max-w-prose` on the label column: the switch is pushed to the far
            edge by `justify-between`, so without a measure a one-sentence hint
            runs the full width of a settings card. */}
        <label htmlFor={id} className="flex max-w-prose flex-col gap-[2px]">
          <span className="text-small font-medium text-ink-graphite">{label}</span>
          {hint && <span className="text-small text-ink-mute">{hint}</span>}
        </label>
        <RSwitch.Root
          id={id}
          checked={checked}
          onCheckedChange={onCheckedChange}
          disabled={disabled}
          aria-invalid={error ? true : undefined}
          aria-describedby={describedBy}
          className="relative h-[24px] w-[44px] shrink-0 rounded-pill bg-hairline-strong transition-colors duration-almanac ease-almanac focus-visible:shadow-focus data-[state=checked]:bg-accent-blue disabled:opacity-40"
        >
          <RSwitch.Thumb className="block h-[20px] w-[20px] translate-x-[2px] rounded-pill bg-canvas-paper transition-transform duration-almanac ease-almanac data-[state=checked]:translate-x-[22px] rtl:data-[state=checked]:translate-x-[-22px]" />
        </RSwitch.Root>
      </div>
      {note && (
        <p id={noteId} className="max-w-prose text-small text-ink-mute">
          {note}
        </p>
      )}
      {error && (
        <p id={errorId} className="max-w-prose text-small text-signal-error" role="alert">
          {error}
        </p>
      )}
    </div>
  );
}
